import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/auth';
import {
  Search, Loader2, Plus, ShieldAlert, ShieldCheck, Tag,
  Truck, Edit3, Check, X, FileText, ExternalLink, ArrowRight, RefreshCw
} from 'lucide-react';
import type { Order } from '@zarin/types';

interface ExtendedOrder extends Order {
  fraudScore?: number;
  fraudSignals?: Array<{ code: string; message: string; severity: 'low' | 'medium' | 'high' }>;
}

interface DraftOrder {
  id: string;
  draftNumber: number;
  status: string;
  customerEmail: string | null;
  customerName: string | null;
  customerPhone: string | null;
  items: Array<{
    variantId: string;
    productId: string;
    title: string;
    variantTitle: string;
    quantity: number;
    priceMinor: number;
  }>;
  subtotalMinor: number;
  discountMinor: number;
  shippingMinor: number;
  totalMinor: number;
  notes: string | null;
  orderId: string | null;
  createdAt: string;
}

interface ShippingLabel {
  id: string;
  carrier: string;
  serviceLevel: string;
  trackingNumber: string;
  trackingUrl: string | null;
  labelUrl: string;
  rateMinor: number;
  createdAt: string;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending:     { bg: 'rgba(232,163,61,0.1)',   color: '#E8A33D' },
  confirmed:   { bg: 'rgba(91,155,213,0.1)',   color: '#5B9BD5' },
  processing:  { bg: 'rgba(91,155,213,0.1)',   color: '#5B9BD5' },
  shipped:     { bg: 'rgba(16,185,129,0.1)',   color: '#10B981' },
  delivered:   { bg: 'rgba(76,175,125,0.15)',  color: '#4CAF7D' },
  cancelled:   { bg: 'rgba(239,68,68,0.1)',    color: '#EF4444' },
  refunded:    { bg: 'rgba(100,100,100,0.1)',  color: '#8A8A8E' },
};

const PAYMENT_COLORS: Record<string, { bg: string; color: string }> = {
  pending:            { bg: 'rgba(232,163,61,0.1)', color: '#E8A33D' },
  paid:               { bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
  partially_refunded: { bg: 'rgba(91,155,213,0.1)', color: '#5B9BD5' },
  refunded:           { bg: 'rgba(100,100,100,0.1)', color: '#8A8A8E' },
  failed:             { bg: 'rgba(239,68,68,0.1)',   color: '#EF4444' },
};

export default function OrdersPage() {
  const [activeTab,    setActiveTab]    = useState<'orders' | 'drafts'>('orders');
  const [orders,       setOrders]       = useState<ExtendedOrder[]>([]);
  const [drafts,       setDrafts]       = useState<DraftOrder[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [status,       setStatus]       = useState('');
  const [cursor,       setCursor]       = useState<string | null>(null);
  const [hasMore,      setHasMore]      = useState(false);
  const [fetchError,   setFetchError]   = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  // Draft Order Creation Modal
  const [draftModal,   setDraftModal]   = useState(false);
  const [draftCustName, setDraftCustName] = useState('');
  const [draftCustEmail, setDraftCustEmail] = useState('');
  const [draftCustPhone, setDraftCustPhone] = useState('');
  const [draftItemName, setDraftItemName] = useState('Flax Linen Relaxed Shirt - Natural Beige');
  const [draftItemQty, setDraftItemQty]   = useState(1);
  const [draftItemPrice, setDraftItemPrice] = useState(4800);
  const [draftDiscount, setDraftDiscount] = useState(0);
  const [draftNotes, setDraftNotes]       = useState('');
  const [savingDraft, setSavingDraft]     = useState(false);

  // Carrier Label Modal
  const [labelModalOrder, setLabelModalOrder] = useState<ExtendedOrder | null>(null);
  const [carrierName, setCarrierName]     = useState('Blue Dart');
  const [serviceLevel, setServiceLevel]   = useState('Apex Air Express');
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [createdLabel, setCreatedLabel]   = useState<ShippingLabel | null>(null);

  // Edit Lines Modal
  const [editLinesOrder, setEditLinesOrder] = useState<ExtendedOrder | null>(null);
  const [editingLines, setEditingLines]   = useState<Array<{ variantId: string; quantity: number; priceMinor: number }>>([]);
  const [savingEditLines, setSavingEditLines] = useState(false);

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    setFetchError(null);
    try {
      if (activeTab === 'orders') {
        const params = new URLSearchParams({ limit: '25' });
        if (search) params.set('search', search);
        if (status) params.set('status', status);
        if (!reset && cursor) params.set('cursor', cursor);

        const res = await apiFetch<{ data: ExtendedOrder[]; meta: { cursor: string | null; hasMore: boolean } }>(`/orders?${params}`);
        setOrders(prev => reset ? res.data : [...prev, ...res.data]);
        setCursor(res.meta.cursor);
        setHasMore(res.meta.hasMore);
      } else {
        const res = await apiFetch<{ data: DraftOrder[] }>('/orders/drafts');
        setDrafts(res.data);
      }
      setLastRefreshed(new Date());
    } catch (e: any) {
      console.error('Failed to load orders telemetry:', e);
      setFetchError(e?.message || 'Failed to load orders from atelier API.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, status, cursor]);

  useEffect(() => { load(true); }, [activeTab, search, status]);

  // Real-time polling: automatically check for new orders every 10 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      load(true);
    }, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const fmt = (n: number) => `₹${(n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const badge = (map: Record<string, { bg: string; color: string }>, key: string, label?: string) => {
    const s = map[key] ?? { bg: 'rgba(100,100,100,0.1)', color: '#8A8A8E' };
    return <span style={{ ...styles.badge, background: s.bg, color: s.color }}>{(label ?? key).replace(/_/g, ' ')}</span>;
  };

  const fraudBadge = (score?: number, signals?: Array<{ code: string; message: string }>) => {
    const s = score ?? 0;
    if (s >= 60) {
      return (
        <span style={{ ...styles.badge, background: 'rgba(239,68,68,0.15)', color: '#EF4444', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={signals?.map(sig => sig.message).join(' | ')}>
          <ShieldAlert size={12} /> High Risk ({s})
        </span>
      );
    }
    if (s >= 30) {
      return (
        <span style={{ ...styles.badge, background: 'rgba(232,163,61,0.15)', color: '#E8A33D', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={signals?.map(sig => sig.message).join(' | ')}>
          <ShieldAlert size={12} /> Review ({s})
        </span>
      );
    }
    return (
      <span style={{ ...styles.badge, background: 'rgba(16,185,129,0.15)', color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ShieldCheck size={12} /> Safe
      </span>
    );
  };

  // Create Draft Order
  const handleCreateDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingDraft(true);
    try {
      await apiFetch('/orders/drafts', {
        method: 'POST',
        body: JSON.stringify({
          customerName:  draftCustName || null,
          customerEmail: draftCustEmail || null,
          customerPhone: draftCustPhone || null,
          items: [
            {
              variantId:    'var_default',
              productId:    'prod_default',
              title:        draftItemName,
              variantTitle: 'Standard Edition',
              quantity:     Number(draftItemQty),
              priceMinor:   Math.round(draftItemPrice * 100),
            },
          ],
          discountMinor: Math.round(draftDiscount * 100),
          notes: draftNotes || 'Phone/Social sale created by atelier staff',
        }),
      });

      setDraftModal(false);
      setDraftCustName('');
      setDraftCustEmail('');
      load(true);
    } catch (err) {
      console.error('Failed to create draft:', err);
    } finally {
      setSavingDraft(false);
    }
  };

  // Complete Draft Order
  const handleCompleteDraft = async (id: string) => {
    if (!confirm('Convert this draft order into an active confirmed order?')) return;
    try {
      await apiFetch(`/orders/drafts/${id}/complete`, { method: 'POST' });
      alert('Draft order converted to active order!');
      load(true);
    } catch (err) {
      console.error('Failed to complete draft:', err);
    }
  };

  // Generate Carrier Label
  const handleGenerateLabel = async () => {
    if (!labelModalOrder) return;
    setGeneratingLabel(true);
    try {
      const res = await apiFetch<{ data: ShippingLabel }>(`/orders/${labelModalOrder.id}/shipping-label`, {
        method: 'POST',
        body: JSON.stringify({ carrier: carrierName, serviceLevel }),
      });
      setCreatedLabel(res.data);
      load(true);
    } catch (err) {
      console.error('Failed to generate label:', err);
    } finally {
      setGeneratingLabel(false);
    }
  };

  // Open Edit Lines Modal
  const openEditLines = (order: ExtendedOrder) => {
    setEditLinesOrder(order);
    setEditingLines(order.lines.map(l => ({
      variantId:  l.variantId,
      quantity:   l.quantity,
      priceMinor: l.priceMinor,
    })));
  };

  const handleSaveEditLines = async () => {
    if (!editLinesOrder) return;
    setSavingEditLines(true);
    try {
      await apiFetch(`/orders/${editLinesOrder.id}/edit-lines`, {
        method: 'PATCH',
        body: JSON.stringify({
          lines: editingLines,
          reason: 'Staff modified size/quantity per customer inquiry',
        }),
      });
      setEditLinesOrder(null);
      alert('Order line items updated and inventory recalculated!');
      load(true);
    } catch (err) {
      console.error('Failed to update lines:', err);
    } finally {
      setSavingEditLines(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Orders & Fulfillment Telemetry</h1>
          <p style={styles.pageSubtitle}>
            Real-time fulfillment, carrier label generation, draft orders & fraud signals
            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--admin-text-subtle)' }}>
              · Last sync: {lastRefreshed.toLocaleTimeString()}
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Refresh / Sync Button */}
          <button
            style={{
              ...styles.tabBtn,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: loading ? 'wait' : 'pointer',
              background: 'var(--admin-bg-secondary)',
              border: '1px solid var(--admin-border)',
              color: 'var(--admin-text)',
            }}
            onClick={() => load(true)}
            disabled={loading}
            title="Sync orders immediately with backend"
          >
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            <span>{loading ? 'Syncing…' : 'Sync Orders'}</span>
          </button>

          {/* Tabs */}
          <div style={styles.tabGroup}>
            <button
              style={{ ...styles.tabBtn, ...(activeTab === 'orders' ? styles.tabBtnActive : {}) }}
              onClick={() => setActiveTab('orders')}
            >
              Customer Orders ({orders.length})
            </button>
            <button
              style={{ ...styles.tabBtn, ...(activeTab === 'drafts' ? styles.tabBtnActive : {}) }}
              onClick={() => setActiveTab('drafts')}
            >
              Draft Orders & Social Sales ({drafts.length})
            </button>
          </div>

          <button style={styles.addBtn} onClick={() => setDraftModal(true)}>
            <Plus size={15} /> New Draft Order
          </button>
        </div>
      </div>

      {fetchError && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 8,
          color: '#EF4444',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>{fetchError}</span>
          <button
            onClick={() => load(true)}
            style={{
              background: '#EF4444',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '4px 12px',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {activeTab === 'orders' && (
        <>
          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <Search size={15} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search by order #, email, phone, tracking number…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select style={styles.select} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Payment</th>
                  <th style={styles.th}>Fraud Risk</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Tracking / Labels</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && orders.length === 0 ? (
                  <tr><td colSpan={8} style={styles.loadingCell}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={8} style={styles.emptyCell}>No orders found</td></tr>
                ) : orders.map(o => (
                  <tr key={o.id} style={styles.tr}>
                    <td style={styles.td}><span style={styles.orderNum}>#{o.orderNumber}</span></td>
                    <td style={styles.td}>
                      <p style={styles.customerEmail}>{o.email ?? '—'}</p>
                      {o.phone && <p style={styles.customerPhone}>{o.phone}</p>}
                    </td>
                    <td style={styles.td}>{badge(STATUS_COLORS,  o.status)}</td>
                    <td style={styles.td}>{badge(PAYMENT_COLORS, o.paymentStatus)}</td>
                    <td style={styles.td}>{fraudBadge(o.fraudScore, o.fraudSignals)}</td>
                    <td style={styles.td}><span style={styles.total}>{fmt(o.totalMinor)}</span></td>
                    <td style={styles.td}>
                      {o.trackingNumber ? (
                        <a
                          href={o.trackingUrl || `https://track.zarinwear.com/${o.trackingNumber}`}
                          target="_blank"
                          rel="noreferrer"
                          style={styles.trackingLink}
                        >
                          <Truck size={13} /> {o.trackingNumber}
                        </a>
                      ) : (
                        <span style={{ fontSize: 12, color: '#8A8A8E' }}>Unfulfilled</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          style={styles.actionBtn}
                          title="Generate Carrier Shipping Label"
                          onClick={() => { setLabelModalOrder(o); setCreatedLabel(null); }}
                        >
                          <Truck size={13} /> Label
                        </button>
                        {o.fulfillmentStatus !== 'fulfilled' && (
                          <button
                            style={styles.actionBtn}
                            title="Edit Order Line Items"
                            onClick={() => openEditLines(o)}
                          >
                            <Edit3 size={13} /> Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Tab 2: Draft Orders ── */}
      {activeTab === 'drafts' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Draft #</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Items Count</th>
                <th style={styles.th}>Total Value</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {drafts.length === 0 ? (
                <tr><td colSpan={7} style={styles.emptyCell}>No draft orders. Create one for phone/social sales.</td></tr>
              ) : drafts.map(d => (
                <tr key={d.id} style={styles.tr}>
                  <td style={styles.td}><span style={styles.orderNum}>DRF-#{d.draftNumber}</span></td>
                  <td style={styles.td}>
                    <div style={{ color: '#FFF' }}>{d.customerName || 'Anonymous Client'}</div>
                    <div style={{ fontSize: 11, color: '#8A8A8E' }}>{d.customerEmail || d.customerPhone || '—'}</div>
                  </td>
                  <td style={styles.td}><span style={{ color: '#AAA' }}>{d.items.length} items</span></td>
                  <td style={styles.td}><span style={styles.total}>{fmt(d.totalMinor)}</span></td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, background: d.status === 'open' ? 'rgba(232,163,61,0.1)' : 'rgba(16,185,129,0.1)', color: d.status === 'open' ? '#E8A33D' : '#10B981' }}>
                      {d.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={styles.td}><span style={{ fontSize: 12, color: '#8A8A8E' }}>{new Date(d.createdAt).toLocaleDateString('en-IN')}</span></td>
                  <td style={styles.td}>
                    {d.status === 'open' ? (
                      <button style={styles.completeBtn} onClick={() => handleCompleteDraft(d.id)}>
                        <Check size={13} /> Complete Order
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: '#10B981' }}>Converted #{d.orderId}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal: Create Draft Order ── */}
      {draftModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Create Draft Order (Phone & Social Sales)</h3>
              <button style={styles.closeBtn} onClick={() => setDraftModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateDraft} style={styles.modalBody}>
              <div style={styles.formRow}>
                <label style={styles.label}>Customer Name</label>
                <input style={styles.input} value={draftCustName} onChange={e => setDraftCustName(e.target.value)} placeholder="e.g. Priya Sharma" />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Customer Email</label>
                <input style={styles.input} type="email" value={draftCustEmail} onChange={e => setDraftCustEmail(e.target.value)} placeholder="priya@example.com" />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Phone Number</label>
                <input style={styles.input} value={draftCustPhone} onChange={e => setDraftCustPhone(e.target.value)} placeholder="+91 98765 43210" />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Garment Selection</label>
                <input style={styles.input} value={draftItemName} onChange={e => setDraftItemName(e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={styles.label}>Quantity</label>
                  <input style={styles.input} type="number" min={1} value={draftItemQty} onChange={e => setDraftItemQty(Number(e.target.value))} />
                </div>
                <div>
                  <label style={styles.label}>Unit Price (₹ INR)</label>
                  <input style={styles.input} type="number" min={0} value={draftItemPrice} onChange={e => setDraftItemPrice(Number(e.target.value))} />
                </div>
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Discount (₹ INR)</label>
                <input style={styles.input} type="number" min={0} value={draftDiscount} onChange={e => setDraftDiscount(Number(e.target.value))} />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Staff Notes</label>
                <textarea style={styles.textarea} value={draftNotes} onChange={e => setDraftNotes(e.target.value)} placeholder="Special delivery instructions or phone order notes…" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" style={styles.cancelBtn} onClick={() => setDraftModal(false)}>Cancel</button>
                <button type="submit" style={styles.submitBtn} disabled={savingDraft}>
                  {savingDraft ? 'Saving Draft…' : 'Create Draft'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Carrier Shipping Label ── */}
      {labelModalOrder && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Purchase Carrier Shipping Label: #{labelModalOrder.orderNumber}</h3>
              <button style={styles.closeBtn} onClick={() => setLabelModalOrder(null)}><X size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              {!createdLabel ? (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Select Integrated Carrier (Step 2.8)</label>
                    <select style={styles.input} value={carrierName} onChange={e => setCarrierName(e.target.value)}>
                      <option value="Blue Dart">Blue Dart Express</option>
                      <option value="Delhivery">Delhivery Surface Standard</option>
                      <option value="DHL">DHL Express Global</option>
                      <option value="Shippo">Shippo / EasyPost Local Express</option>
                    </select>
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Service Level</label>
                    <input style={styles.input} value={serviceLevel} onChange={e => setServiceLevel(e.target.value)} />
                  </div>
                  <div style={{ background: '#202024', padding: 14, borderRadius: 6, margin: '16px 0', fontSize: 13, color: '#AAA' }}>
                    <strong>Destination:</strong> {labelModalOrder.shippingAddress?.city}, {labelModalOrder.shippingAddress?.state} ({labelModalOrder.shippingAddress?.postalCode})<br />
                    <strong>Order Value:</strong> {fmt(labelModalOrder.totalMinor)}
                  </div>
                  <button style={styles.submitBtn} onClick={handleGenerateLabel} disabled={generatingLabel}>
                    {generatingLabel ? 'Generating Carrier Label…' : 'Purchase & Generate PDF Label'}
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ color: '#10B981', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>✅ Shipping Label Generated!</div>
                  <p style={{ color: '#FFF', fontSize: 14 }}>Tracking Number: <strong>{createdLabel.trackingNumber}</strong></p>
                  <p style={{ color: '#8A8A8E', fontSize: 12 }}>Carrier: {createdLabel.carrier} • Rate: {fmt(createdLabel.rateMinor)}</p>
                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 12 }}>
                    <a href={createdLabel.labelUrl} target="_blank" rel="noreferrer" style={styles.submitBtn}>
                      <FileText size={14} /> Open PDF Label
                    </a>
                    <button style={styles.cancelBtn} onClick={() => setLabelModalOrder(null)}>Done</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Edit Order Lines ── */}
      {editLinesOrder && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Edit Order Items: #{editLinesOrder.orderNumber}</h3>
              <button style={styles.closeBtn} onClick={() => setEditLinesOrder(null)}><X size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: 12, color: '#8A8A8E', marginBottom: 16 }}>
                Modify quantities or swap variant sizes. The ledger will automatically adjust committed stock and recalculate totals.
              </p>
              {editLinesOrder.lines.map((l, idx) => (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, background: '#202024', padding: 12, borderRadius: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#FFF', fontWeight: 500, fontSize: 13 }}>{l.title}</div>
                    <div style={{ fontSize: 11, color: '#8A8A8E' }}>Size: {l.variantTitle}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#8A8A8E', display: 'block' }}>Qty</label>
                    <input
                      type="number"
                      min={1}
                      value={editingLines[idx]?.quantity ?? l.quantity}
                      onChange={e => {
                        const val = Number(e.target.value);
                        setEditingLines(prev => {
                          const n = [...prev];
                          n[idx] = { ...n[idx]!, quantity: val };
                          return n;
                        });
                      }}
                      style={{ ...styles.input, width: 70 }}
                    />
                  </div>
                  <div style={{ fontWeight: 600, color: '#FFF', minWidth: 80, textAlign: 'right' }}>
                    {fmt(l.priceMinor * (editingLines[idx]?.quantity ?? l.quantity))}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button style={styles.cancelBtn} onClick={() => setEditLinesOrder(null)}>Cancel</button>
                <button style={styles.submitBtn} onClick={handleSaveEditLines} disabled={savingEditLines}>
                  {savingEditLines ? 'Updating…' : 'Save Line Modifications'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page:          { maxWidth: 1200, margin: '0 auto', color: '#E5E5E5', width: '100%' },
  pageHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  pageTitle:     { fontSize: 24, fontWeight: 600, color: '#FFF', marginBottom: 4, fontFamily: "'Playfair Display', serif" },
  pageSubtitle:  { fontSize: 13, color: '#8A8A8E' },
  tabGroup:      { display: 'flex', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, padding: 3, flexWrap: 'wrap' },
  tabBtn:        { padding: '6px 14px', background: 'transparent', border: 'none', color: '#8A8A8E', fontSize: 12, borderRadius: 4, cursor: 'pointer' },
  tabBtnActive:  { background: '#2E2E34', color: '#FFF', fontWeight: 600 },
  addBtn:        { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#C9A84C', color: '#1A1A1A', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  toolbar:       { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  searchWrap:    { position: 'relative', flex: 1, minWidth: 200 },
  searchIcon:    { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A8A8E' },
  searchInput:   { width: '100%', padding: '9px 12px 9px 34px', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, fontSize: 13, color: '#FFF', outline: 'none' },
  select:        { padding: '9px 12px', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, fontSize: 13, color: '#FFF', outline: 'none', cursor: 'pointer' },
  tableWrap:     { background: '#1E1E22', border: '1px solid #333', borderRadius: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' },
  table:         { width: '100%', borderCollapse: 'collapse', minWidth: 640 },
  th:            { padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#8A8A8E', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #333', background: '#18181B' },
  tr:            { borderBottom: '1px solid #28282C' },
  td:            { padding: '12px 16px', verticalAlign: 'middle', fontSize: 13 },
  loadingCell:   { padding: 40, textAlign: 'center', color: '#8A8A8E' },
  emptyCell:     { padding: 40, textAlign: 'center', color: '#8A8A8E', fontSize: 13 },
  badge:         { display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' },
  orderNum:      { fontWeight: 600, color: '#C9A84C' },
  customerEmail: { color: '#FFF', marginBottom: 2 },
  customerPhone: { fontSize: 11, color: '#8A8A8E' },
  total:         { fontWeight: 600, color: '#FFF' },
  trackingLink:  { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#5B9BD5', textDecoration: 'none', fontSize: 12 },
  actionBtn:     { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', background: '#28282C', border: '1px solid #38383E', color: '#FFF', borderRadius: 4, fontSize: 11, cursor: 'pointer' },
  completeBtn:   { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#10B981', border: 'none', color: '#FFF', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  modalOverlay:  { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 },
  modalBox:      { background: '#18181B', border: '1px solid #333', borderRadius: 8, width: 540, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
  modalHeader:   { padding: '16px 20px', borderBottom: '1px solid #2E2E34', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle:    { margin: 0, fontSize: 16, fontWeight: 600, color: '#FFF' },
  closeBtn:      { background: 'transparent', border: 'none', color: '#8A8A8E', cursor: 'pointer' },
  modalBody:     { padding: 20, overflowY: 'auto' },
  formRow:       { marginBottom: 14 },
  label:         { display: 'block', fontSize: 12, color: '#8A8A8E', marginBottom: 6 },
  input:         { width: '100%', padding: '8px 12px', background: '#141416', border: '1px solid #333', borderRadius: 6, color: '#FFF', fontSize: 13, outline: 'none' },
  textarea:      { width: '100%', padding: '8px 12px', background: '#141416', border: '1px solid #333', borderRadius: 6, color: '#FFF', fontSize: 13, outline: 'none', height: 70, resize: 'vertical' },
  cancelBtn:     { padding: '8px 16px', background: 'transparent', border: '1px solid #444', color: '#AAA', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  submitBtn:     { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#C9A84C', border: 'none', color: '#000', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' },
};
